import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "@/lib/firebase";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { getToken } from "firebase/messaging";
import { getMessagingInstance } from "@/lib/firebase";
import { signInWithGoogle } from "@/lib/googleSignIn";
import { resolveLocale } from "@/lib/i18n/resolveLocale";
import { useTranslations } from "@/contexts/I18nContext";
import { userFacingMessage } from "@/lib/errors/userFacingError";
import { AppDownloadBanner } from "@/components/features/AppDownloadBanner";

export function RegisterPage() {
  const navigate = useNavigate();
  const t = useTranslations();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [nickname, setNickname] = useState("");
  const [memberType, setMemberType] = useState<"adult" | "child" | "elderly_parent">("adult");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setError("");
    setGoogleLoading(true);
    try {
      const { isNewUser } = await signInWithGoogle();
      const pending = window.localStorage.getItem("pending_invite_code");
      if (pending) {
        navigate(`/join?code=${encodeURIComponent(pending)}`);
      } else {
        navigate(isNewUser ? "/onboarding" : "/dashboard");
      }
    } catch (err: unknown) {
      setError(userFacingMessage(err, t("login.errors.googleFailed")));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError(t("register.errors.passwordsMismatch"));
      return;
    }

    setLoading(true);

    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const userId = result.user.uid;

      // Create member_profiles document
      // Use the auth UID as the document ID for consistency with iOS
      const resolved = resolveLocale({
        navigatorLanguage:
          typeof navigator !== "undefined" ? navigator.language : null,
      });
      await setDoc(doc(db, "member_profiles", userId), {
        authUid: userId,
        nickname: nickname,
        name: nickname,
        memberType,
        isActive: true,
        households: [],
        locale: resolved.locale,
        createdAt: Timestamp.now(),
        createdBy: userId,
      });

      // Register FCM token
      try {
        const messaging = await getMessagingInstance();
        if (messaging) {
          const token = await getToken(messaging, {
            vapidKey: import.meta.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
          });
          if (token) {
            await setDoc(
              doc(db, "member_profiles", userId),
              { user_devices: [token] },
              { merge: true }
            );
          }
        }
      } catch {
        // FCM is best-effort
      }

      // Honor a pending invite if one was stashed by /join while signed out
      // (Sub-flow A/B). Otherwise drop the new user straight into the
      // onboarding gate — the (app)/ household guard would redirect there
      // anyway, but routing directly avoids a flash.
      const pending = window.localStorage.getItem("pending_invite_code");
      if (pending) {
        navigate(`/join?code=${encodeURIComponent(pending)}`);
      } else {
        navigate("/onboarding");
      }
    } catch (err: unknown) {
      setError(userFacingMessage(err, t("register.errors.createFailed")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-sm">
      <div className="mb-6">
        <AppDownloadBanner />
      </div>

      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold" style={{ color: "var(--accent-color, #00C7BE)" }}>
          {t("register.title")}
        </h1>
        <p className="text-gray-500 mt-2">{t("register.subtitle")}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="nickname" className="block text-sm font-medium mb-1">
            {t("register.nickname.label")}
          </label>
          <input
            id="nickname"
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            required
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color,#00C7BE)]"
            placeholder={t("register.nickname.placeholder")}
          />
        </div>

        <div>
          <span className="block text-sm font-medium mb-1">
            {t("register.memberType.label")}
          </span>
          <div className="flex gap-2">
            {(["adult", "child", "elderly_parent"] as const).map((value) => {
              const active = memberType === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setMemberType(value)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    active
                      ? "bg-[color:var(--accent-color,#00C7BE)] text-white border-[color:var(--accent-color,#00C7BE)]"
                      : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-700"
                  }`}
                >
                  {t(`register.memberType.${value}`)}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-1">
            {t("auth.email.label")}
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color,#00C7BE)]"
            placeholder={t("auth.email.placeholder")}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium mb-1">
            {t("auth.password.label")}
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color,#00C7BE)]"
            placeholder="••••••••"
          />
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1">
            {t("register.confirmPassword.label")}
          </label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color,#00C7BE)]"
            placeholder="••••••••"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full py-2.5 rounded-lg text-white font-semibold transition-opacity disabled:opacity-50"
          style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
        >
          {loading ? t("register.creating") : t("register.create")}
        </button>

        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
          <span className="text-xs text-gray-400 uppercase tracking-wider">{t("common.or")}</span>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
        </div>

        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={googleLoading || loading}
          className="w-full py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
            <path fill="#FBBC05" d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" />
            <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" />
          </svg>
          {googleLoading ? t("login.signingIn") : t("login.continueWithGoogle")}
        </button>
      </form>

      <p className="text-center text-sm text-gray-500 mt-6">
        {t("register.alreadyHaveAccount")}{" "}
        <Link to="/login" className="text-[color:var(--accent-color,#00C7BE)] font-medium">
          {t("login.signIn")}
        </Link>
      </p>
    </div>
  );
}
